/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * MCQ Component
 *
 * @file McqComponent.jsx
 * @author Murhsid Ahamedh
 */

/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { IconButton, Menu, Button, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import StatusTagMenu from './StatusTagMenu';
import {
  AskAiCtaHeaderPill,
  AskAiCtaIcon,
  AttemptsCount,
  ChapterHeader,
  ChapterWrapper,
  Content,
  Correct,
  CorrectLabel,
  CorrectSubLabel,
  CourseMenuRoot,
  Footer,
  FooterInfoIconButton,
  FooterContainer,
  FooterLeftCluster,
  FooterMetaWrapper,
  FooterWithOnlyChild,
  Header,
  HeaderRow,
  Heading,
  HeadingWrapper,
  MenuItemStyled,
  OptionContent,
  OptionIcon,
  OptionLabel,
  OptionPrefix,
  OptionRow,
  Options,
  PrefixCorrect,
  PrefixWrong,
  Question,
  QuestionNumberWrapper,
  QuestionRow,
  QuestionStem,
  QuestionTitle,
  ReviewAssignmentLink,
  Root,
  StatusIconCorrect,
  StatusIconWrong,
  StatusTagMenuContainer,
  ThreeDotMenuIcon,
  Wrong,
  WrongLabel,
  WrongSubLabel
} from './Styles/McqStyles';
import messages from '../../common/defaultMessages';
import { FOOTER_CTA_LINKS, PERFORMANCE_STATUS } from '../../common/constants';
import MathTextComponent from './MathTextComponent';
import FooterDeatilsCard from './FooterDeatilsCard';
import Icon from '../icons/Icon';

const McqComponent = ({
  parentApp,
  question,
  questionNumber,
  options,
  selectedOptions,
  chapterTitle,
  attempts,
  isShowAttempt,
  fromStudent,
  status,
  onStatusChange,
  assignmentName,
  sections,
  intl,
  footer,
  footerDialogDetails,
  footerCTALinks,
  threeDotMenuItems,
  questionDetails = {}
}) => {
  const [isFooterDialogOpen, setIsFooterDialogOpen] = useState(false);
  const [threeDotMenuAnchorEl, setThreeDotMenuAnchorEl] = useState(null);
  const isThreeDotMenuOpen = Boolean(threeDotMenuAnchorEl);

  // Track status locally so the selected tag updates immediately.
  const [currentStatus, setCurrentStatus] = React.useState(status);
  React.useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  const toKey = (v) => (v === null || v === undefined ? null : String(v).trim().toLowerCase());
  const normalized = Array.isArray(selectedOptions) ? selectedOptions : [];

  const { selectedIdSet } = React.useMemo(() => {
    const idSet = new Set(
      normalized
        .map((item) => (typeof item === 'object' ? toKey(item.id) : null))
        .filter((v) => v !== null)
    );

    return { selectedIdSet: idSet };
  }, [normalized]);

  if (parentApp && parentApp.toLowerCase() !== 'aet') {
    return null;
  }

  if (!question || !Array.isArray(options)) return null;

  const isSelectedOption = (opt) => (
    selectedIdSet.has(toKey(opt.id))
  );

  const openFooterDialog = () => {
    setIsFooterDialogOpen(true);
  };

  const closeFooterDialog = () => {
    setIsFooterDialogOpen(false);
  };

  const getLetter = (index) => String.fromCharCode(65 + index);

  const renderFooter = () => {
    if (!footer) return null;

    const shouldShowFooterDialog =
    footerDialogDetails &&
    (footerDialogDetails.title || footerDialogDetails.content);

    return (
      <FooterMetaWrapper>
        <Footer>
          {footer}

          {shouldShowFooterDialog && (
            <FooterInfoIconButton
              aria-label="More information"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openFooterDialog();
              }}
            >
              <Icon name="FooterInfoIcon" style={{ fontSize: 16 }} />
            </FooterInfoIconButton>
          )}
        </Footer>

        {shouldShowFooterDialog && (
          <FooterDeatilsCard
            open={isFooterDialogOpen}
            onClose={closeFooterDialog}
            title={footerDialogDetails.title}
            content={footerDialogDetails.content}
            confirmText={footerDialogDetails.confirmText || 'Confirm'}
            cancelText={footerDialogDetails.cancelText || null}
            showCloseButton={footerDialogDetails.showCloseButton ?? true}
            onConfirm={footerDialogDetails.onConfirm || closeFooterDialog}
          />
        )}
      </FooterMetaWrapper>
    );
  };

  /**
   * Render ask AI button
   * @param {Object} item
   * @returns {React.ReactNode}
   */
  const renderAskAiButton = (item) => {
    if (!item) return null;

    return (
      <AskAiCtaHeaderPill
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          item?.handleOnClick?.(question, questionDetails?.questionId);
        }}
        aria-label={item.label}
      >
        {item.iconName && (
          <Icon name={item.iconName} component={AskAiCtaIcon} />
        )}
        {item.label}
      </AskAiCtaHeaderPill>
    );
  };

  /**
   * Render footer CTA item
   * @param {Object} item
   * @returns {React.ReactNode}
   */
  const renderFooterCtaItem = (item) => {
    if (!item) {
      return null;
    }

    if (item.id === FOOTER_CTA_LINKS.ASK_AI) {
      return renderAskAiButton(item);
    }

    return (
      <ReviewAssignmentLink
        key={item.title}
        component={Button}
        onClick={(e) => {
          e.stopPropagation();
          item?.handleOnClick?.();
        }}
      >
        {item.title}
      </ReviewAssignmentLink>
    );
  };

  /**
   * Render footer CTA links
   * @returns {React.ReactNode}
   */
  const renderFooterCTALinks = () => {
    if (!footerCTALinks || !footerCTALinks.length) {
      return null;
    }

    return (
      <FooterLeftCluster>
        {footerCTALinks.map((item) => renderFooterCtaItem(item))}
      </FooterLeftCluster>
    );
  };

  /**
   * Handle three dot menu close
   * @returns {void}
   */
  const handleThreeDotMenuClose = () => {
    setThreeDotMenuAnchorEl(null);
  };

  /**
   * Handle menu open
   * @param {React.MouseEvent<HTMLButtonElement>} event
   * @returns {void}
   */
  const handleThreeDotMenuOpen = (event) => {
    setThreeDotMenuAnchorEl(event.currentTarget);
  };

  const renderThreeDotMenu = () => {
    if (!threeDotMenuItems || !threeDotMenuItems.length) {
      return null;
    }

    return (
      <Menu
        anchorEl={threeDotMenuAnchorEl}
        keepMounted
        open={isThreeDotMenuOpen}
        onClose={handleThreeDotMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slots={{
          paper: CourseMenuRoot,
          list: CourseMenuRoot
        }}
      >
        {threeDotMenuItems.map((item) => (
          <MenuItemStyled
            key={item.label}
            onClick={() => {
              handleThreeDotMenuClose();
              item.callback(question);
            }}
          >
            {item.iconName && <Icon name={item.iconName} component={ThreeDotMenuIcon} />}
            <Typography>{item.label}</Typography>
          </MenuItemStyled>
        ))}
      </Menu>
    );
  };

  const renderThreeDotMenuIcon = () => {
    if (!threeDotMenuItems || !threeDotMenuItems.length) {
      return null;
    }

    return (
      <Fragment>
        <IconButton
          aria-label="More information"
          size="small"
          onClick={handleThreeDotMenuOpen}
        >
          <Icon name="ThreeDotMenuIcon" style={{ fontSize: 16 }} />
        </IconButton>

        {renderThreeDotMenu()}
      </Fragment>
    );
  };

  return (
    <Root component="section">
      <Header>
        <HeadingWrapper>
          {fromStudent && assignmentName ? (
            <Heading component="h3" variant="body1">
              {assignmentName}
            </Heading>
          ) : null}

          {fromStudent ? (
            (() => {
              const handleSelect = (id, label) => {
                setCurrentStatus(label);
                if (onStatusChange) onStatusChange(id);
              };

              return (
                <StatusTagMenuContainer>
                  <StatusTagMenu selectedTag={currentStatus} sections={sections} onSelect={handleSelect} />
                  {renderThreeDotMenuIcon()}
                </StatusTagMenuContainer>
              );
            })()
          ) : null}
        </HeadingWrapper>
        <HeaderRow>
          {question ? (
            <QuestionStem>
              {questionNumber && (
                <QuestionNumberWrapper>
                  <QuestionTitle>{questionNumber}</QuestionTitle>
                  <QuestionTitle component="span">:</QuestionTitle>
                </QuestionNumberWrapper>
              )}
              <ChapterWrapper>
                <MathTextComponent
                  text={question}
                  component={ChapterHeader}
                  variant="body1"
                  domComponent="h4"
                />
                <QuestionRow>
                  {chapterTitle ? (
                    <Question component="h5" variant="body1">{chapterTitle}</Question>
                  ) : <span />}
                  {(!fromStudent && isShowAttempt) ? (
                    <AttemptsCount component="div">{attempts} {intl.formatMessage(messages.attempts)}</AttemptsCount>
                  ) : null}
                </QuestionRow>
              </ChapterWrapper>
            </QuestionStem>
          ) : <span /> }
        </HeaderRow>
      </Header>
      <Content>
        <Options component="ul">
          {options.map((opt, index) => {
            const isCorrect = !!opt.correct;
            const isSelected = isSelectedOption(opt);

            const RowComponent = isCorrect
              ? Correct
              : (!isCorrect && isSelected)
                ? Wrong
                : OptionRow;

            const PrefixComponent = isCorrect
              ? PrefixCorrect
              : isSelected
                ? PrefixWrong
                : OptionPrefix;

            const LabelComponent = isCorrect
              ? CorrectLabel
              : isSelected
                ? WrongLabel
                : OptionLabel;

            return (
              <RowComponent key={opt.id || opt.value} component="li">
                <PrefixComponent component="span">
                  {getLetter(index)}
                </PrefixComponent>
                <OptionContent>
                  <MathTextComponent
                    text={opt.label || ''}
                    component={LabelComponent}
                  />
                  {isCorrect && (
                  <CorrectSubLabel component="p">{intl.formatMessage(messages.correctAnswer)}</CorrectSubLabel>
                  )}
                  {!isCorrect && isSelected && (
                  <WrongSubLabel component="p">{intl.formatMessage(messages.incorrectAnswer)}</WrongSubLabel>
                  )}
                </OptionContent>
                {isCorrect ? (
                  <OptionIcon component="span" aria-hidden="true">
                    <StatusIconCorrect component="span">
                      <DoneIcon style={{ color: '#FFFFFF', fontSize: 12 }} aria-hidden="true" />
                    </StatusIconCorrect>
                  </OptionIcon>
                ) : isSelected ? (
                  <OptionIcon component="span" aria-hidden="true">
                    <StatusIconWrong component="span">
                      <CloseIcon style={{ color: '#FFFFFF', fontSize: 12 }} aria-hidden="true" />
                    </StatusIconWrong>
                  </OptionIcon>
                ) : null}
              </RowComponent>
            );
          })}
        </Options>
      </Content>
      {footerCTALinks.length ? (
        <FooterContainer>
          {renderFooterCTALinks()}
          {renderFooter()}
        </FooterContainer>
      ) : (
        <FooterWithOnlyChild>
          {renderFooterCTALinks()}
          {renderFooter()}
        </FooterWithOnlyChild>
      )}
    </Root>
  );
};

McqComponent.propTypes = {
  parentApp: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      correct: PropTypes.bool
    })
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  questionNumber: PropTypes.string,
  chapterTitle: PropTypes.string,
  attempts: PropTypes.number,
  isShowAttempt: PropTypes.bool,
  fromStudent: PropTypes.bool,
  status: PropTypes.oneOf([PERFORMANCE_STATUS.NEEDS_REVIEW, PERFORMANCE_STATUS.OKAY, PERFORMANCE_STATUS.GREAT]),
  onStatusChange: PropTypes.func,
  assignmentName: PropTypes.string,
  intl: intlShape.isRequired,
  sections: PropTypes.array,
  footer: PropTypes.string,
  footerDialogDetails: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showCloseButton: PropTypes.bool,
    onConfirm: PropTypes.func
  }),
  footerCTALinks: PropTypes.arrayOf(PropTypes.object),
  threeDotMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      callback: PropTypes.func,
      iconName: PropTypes.string
    })
  ),
  questionDetails: PropTypes.object
};

McqComponent.defaultProps = {
  selectedOptions: [],
  questionNumber: '',
  chapterTitle: '',
  attempts: 0,
  isShowAttempt: false,
  fromStudent: false,
  status: PERFORMANCE_STATUS.NEEDS_REVIEW,
  onStatusChange: undefined,
  assignmentName: '',
  sections: [],
  footer: '',
  footerDialogDetails: {
    title: '',
    content: '',
    confirmText: 'Confirm',
    cancelText: null,
    showCloseButton: false,
    onConfirm: () => {}
  },
  footerCTALinks: [],
  threeDotMenuItems: [],
  questionDetails: {}
};

export default injectIntl(McqComponent);

